import { Building2, Globe, Award, Server, Eye, TrendingUp, Monitor, Target, Zap, Users, Bot, Sparkles } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";

import { PageHero, HeroGlassCard } from "@/components/shared/PageHero";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { GlowCard } from "@/components/shared/GlowCard";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

const WhoWeAre = () => {
  const { language } = useLanguage();
  const { ref: r1 } = useRevealOnScroll<HTMLDivElement>();
  const { ref: r2 } = useRevealOnScroll<HTMLDivElement>();
  const { ref: r3 } = useRevealOnScroll<HTMLDivElement>();
  const { ref: r4 } = useRevealOnScroll<HTMLDivElement>();

  const stats: Array<{ icon: typeof Monitor; value: string; label: string }> = [
    { icon: Monitor, value: "4600+", label: language === "ar" ? "شاشة رقمية" : "Digital Screens" },
    { icon: Eye, value: "4.8M+", label: language === "ar" ? "مشاهدة يومية" : "Daily Views" },
    { icon: TrendingUp, value: "95%", label: language === "ar" ? "رضا العملاء" : "Client Satisfaction" },
    { icon: Users, value: "500+", label: language === "ar" ? "عميل راضي" : "Happy Clients" },
  ];

  type Accent = "blue" | "violet" | "emerald" | "amber";
  const services: Array<{ icon: typeof Server; title: string; desc: string; accent: Accent }> = [
    {
      icon: Server,
      title: language === "ar" ? "إعلانات برمجية" : "Programmatic Ads",
      desc: language === "ar" ? "حلول إعلانية ذكية تستهدف جمهورك بدقة باستخدام الذكاء الاصطناعي" : "Smart AI-powered advertising solutions targeting your audience with precision",
      accent: "blue",
    },
    {
      icon: Globe,
      title: language === "ar" ? "تغطية واسعة" : "Wide Coverage",
      desc: language === "ar" ? "شبكة واسعة من الشاشات الرقمية في جميع أنحاء المملكة العربية السعودية" : "Extensive network of digital displays across the Kingdom of Saudi Arabia",
      accent: "violet",
    },
    {
      icon: Building2,
      title: language === "ar" ? "مواقع استراتيجية" : "Strategic Locations",
      desc: language === "ar" ? "شاشات في أكثر المواقع حيوية وتأثيراً لضمان أقصى وصول للجمهور" : "Displays in the most vibrant and impactful locations ensuring maximum audience reach",
      accent: "emerald",
    },
    {
      icon: Award,
      title: language === "ar" ? "خدمة متميزة" : "Premium Service",
      desc: language === "ar" ? "دعم متكامل وخدمة عملاء متميزة لضمان نجاح حملتك الإعلانية" : "Comprehensive support and premium customer service to ensure your campaign success",
      accent: "amber",
    },
  ];

  const values: Array<{ icon: typeof Target; title: string; desc: string; accent: Accent }> = [
    {
      icon: Target,
      title: language === "ar" ? "الدقة في الاستهداف" : "Strategic Placement",
      desc: language === "ar" ? "نضع إعلاناتك في المواقع الاستراتيجية المناسبة لضمان أقصى تأثير لعلامتك التجارية" : "We place your ads in strategic locations to ensure maximum impact for your brand",
      accent: "blue",
    },
    {
      icon: Zap,
      title: language === "ar" ? "التكنولوجيا المتطورة" : "Advanced Technology",
      desc: language === "ar" ? "منصة مدعومة بالذكاء الاصطناعي توفر تحليلات فورية وتحسين مستمر للحملات" : "AI-powered platform providing real-time analytics and continuous campaign optimization",
      accent: "violet",
    },
    {
      icon: Users,
      title: language === "ar" ? "فريق الخبراء" : "Expert Team",
      desc: language === "ar" ? "فريق متخصص من الخبراء في مجال الإعلانات الرقمية والتسويق الإلكتروني" : "Specialized team of experts in digital advertising",
      accent: "emerald",
    },
  ];

  return (
    <div className="relative min-h-screen">
      <SEO
        title="Who We Are — Fusion Media"
        description="Meet Fusion Media: pioneers of AI-powered digital out-of-home advertising in Saudi Arabia, with 4,600+ screens and millions of daily views."
      />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHero
          eyebrow={language === "ar" ? "أول منصة سعودية شاملة" : "First all-in-one Saudi platform"}
          title={language === "ar" ? "من" : "Who"}
          accent={language === "ar" ? "نحن" : "we are"}
          subtitle={
            language === "ar"
              ? "نحن رواد الإعلانات الرقمية الخارجية في المملكة العربية السعودية، نجمع بين التكنولوجيا المتقدمة والمواقع الاستراتيجية"
              : "Pioneers of digital advertising in Saudi Arabia, combining cutting-edge AI technology with strategic locations across the Kingdom."
          }
          rightSlot={
            <HeroGlassCard>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-semibold text-white/70 uppercase tracking-widest">
                  Live impact
                </div>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-semibold">
                  Active
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {stats.map((s, i) => (
                  <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-3">
                    <div className="text-2xl font-bold text-white tabular-nums">{s.value}</div>
                    <div className="text-[10px] text-white/60 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </HeroGlassCard>
          }
        />

        {/* Mission & Vision */}
        <section ref={r1} className="reveal-on-scroll pb-16 md:pb-24">
          <div className="grid md:grid-cols-2 gap-6">
            <GlowCard icon={Target} accent="blue" title={language === "ar" ? "رؤيتنا" : "Our Vision"}
              description={language === "ar" ? "أن نكون المنصة الرائدة عالمياً في مجال الإعلانات الرقمية الخارجية المدعومة بالذكاء الاصطناعي" : "To be the world's leading platform for AI-powered digital advertising solutions."} />
            <GlowCard icon={Zap} accent="violet" title={language === "ar" ? "مهمتنا" : "Our Mission"}
              description={language === "ar" ? "تمكين المعلنين من الوصول لجمهورهم بطريقة ذكية وفعالة من خلال منصة تقنية متطورة" : "Empowering advertisers to reach their audience intelligently and effectively through an advanced technology platform."} />
          </div>
        </section>

        {/* Services */}
        <section ref={r2} className="reveal-on-scroll pb-16 md:pb-24">
          <SectionHeader
            eyebrow="Services"
            title={language === "ar" ? "خدماتنا المتميزة" : "Our premium services"}
            subtitle={language === "ar" ? "مجموعة شاملة من الخدمات الإعلانية المتطورة" : "A comprehensive range of advanced advertising services designed to maximize your brand impact."}
          />
          <div className="grid gap-6 md:grid-cols-2">
            {services.map((s, i) => (
              <GlowCard key={i} icon={s.icon} accent={s.accent} title={s.title} description={s.desc} />
            ))}
          </div>
        </section>

        {/* Values */}
        <section ref={r3} className="reveal-on-scroll pb-16 md:pb-24">
          <SectionHeader
            title={language === "ar" ? "قيمنا الأساسية" : "Our core values"}
            subtitle={language === "ar" ? "المبادئ التي توجه عملنا" : "Principles that guide our work and define our partnerships."}
          />
          <div className="grid gap-6 md:grid-cols-3">
            {values.map((v, i) => (
              <GlowCard key={i} icon={v.icon} accent={v.accent} title={v.title} description={v.desc} />
            ))}
          </div>
        </section>

        {/* Story */}
        <section ref={r4} className="reveal-on-scroll pb-16 md:pb-24">
          <div className="bg-card border border-border/60 rounded-2xl p-6 md:p-10 shadow-card grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="/images/596b5134-7ad6-42a6-a330-cd5b37b12760.png"
                alt="Company Overview"
                className="object-cover w-full h-full"
              />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-[11px] font-semibold tracking-widest uppercase mb-4">
                <Bot className="w-3 h-3" />
                {language === "ar" ? "قصتنا" : "Our story"}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                {language === "ar" ? "قصة نجاحنا" : "Our success story"}
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                {language === "ar"
                  ? "بدأت رحلتنا برؤية طموحة لتطوير صناعة الإعلانات الرقمية في المملكة العربية السعودية. اليوم، نحن فخورون بكوننا الرواد في هذا المجال."
                  : "Our journey began with an ambitious vision to revolutionize the digital advertising industry in Saudi Arabia. Today, we are proud to be the pioneers in this field."}
              </p>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {language === "ar"
                  ? "نجمع بين الخبرة المحلية والتكنولوجيا العالمية لنقدم حلولاً إعلانية مبتكرة تحقق نتائج استثنائية لعملائنا."
                  : "We combine local expertise with global technology to deliver innovative advertising solutions that achieve exceptional results for our clients."}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default WhoWeAre;
